// @ts-check

/**
 * @file Renders a paginated async project select.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import ModelSelect from "../model-select"
import {Project} from "../../frontend-models/project"
import {MODEL_SELECT_PAGE_SIZE, modelSelectOptionsResult, normalizedSelectValues} from "../model-select-options"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/**
 * @typedef {object} ProjectsSelectProps
 * @property {boolean} [closeOnChange] Close the dropdown after each selection change.
 * @property {string | number | null} [defaultValue] Initial selected project id.
 * @property {(string | number)[]} [defaultValues] Initial selected project ids.
 * @property {string} [label] Select label.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {string} [name] Api Maker form field name.
 * @property {(...args: unknown[]) => unknown} [onChangeValue] Selection change handler.
 * @property {string} [placeholder] Placeholder text.
 * @property {string} testID Stable test id.
 * @property {(string | number)[]} [values] Controlled selected values.
 */

/** @typedef {Record<string, never>} ProjectsSelectState */

/**
 * Project select that loads only the current page of project labels.
 * @augments {ShapeComponent<ProjectsSelectProps, ProjectsSelectState>}
 */
class ProjectsSelect extends ShapeComponent {
  static propTypes = propTypesExact({
    closeOnChange: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    label: PropTypes.string,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChangeValue: PropTypes.func,
    placeholder: PropTypes.string,
    testID: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    )
  })
  static defaultProps = {
    closeOnChange: false,
    defaultValue: undefined,
    defaultValues: undefined,
    label: undefined,
    multiple: false,
    name: undefined,
    onChangeValue: undefined,
    placeholder: undefined,
    values: undefined
  }

  /** Initializes locale support. */
  setup() {
    useLocale()
  }

  /**
   * Renders the select.
   * @returns {ReactNode} Project select.
   */
  render() {
    const {
      closeOnChange,
      defaultValue,
      defaultValues,
      label,
      multiple,
      name,
      onChangeValue,
      placeholder,
      testID,
      values
    } = this.p
    return (
      <ModelSelect
        closeOnChange={closeOnChange}
        defaultValue={defaultValue}
        defaultValues={defaultValues}
        label={label}
        multiple={multiple}
        name={name}
        onChangeValue={onChangeValue}
        options={this.tt.loadOptions}
        placeholder={placeholder || _("Search projects...")}
        testID={testID}
        values={values}
      />
    )
  }

  /**
   * Loads project options for HayaSelect.
   * @param {{page?: number, searchValue?: string, values?: (string | number)[] | string | number}} args Loader args.
   * @returns {Promise<{options: {text: string, value: string}[], page?: number, pageSize?: number, totalCount?: number}>} Paginated options.
   */
  loadOptions = async ({page = 1, searchValue, values} = {}) => {
    const selectedValues = normalizedSelectValues(values)
    if (selectedValues.length > 0) {
      const projects = await Project
        .where({id: selectedValues})
        .select(["id", "name"])
        .order([["name", "asc"], ["id", "asc"]])
        .toArray()
      return modelSelectOptionsResult({
        options: projects.map(projectToOption),
        page: 1,
        totalCount: projects.length
      })
    }

    /** @type {Record<string, unknown>} */
    const ransackParams = {}
    if (searchValue) ransackParams.nameCont = searchValue
    const baseQuery = Project.ransack(ransackParams)
    const [projects, totalCount] = await Promise.all([
      baseQuery
        .clone()
        .select(["id", "name"])
        .order([["name", "asc"], ["id", "asc"]])
        .page(page)
        .perPage(MODEL_SELECT_PAGE_SIZE)
        .toArray(),
      baseQuery.clone().count()
    ])
    return modelSelectOptionsResult({
      options: projects.map(projectToOption),
      page,
      totalCount
    })
  }
}

/**
 * Converts a project model into a select option.
 * @param {InstanceType<typeof Project>} project Project model.
 * @returns {{text: string, value: string}} Select option.
 */
function projectToOption(project) {
  return {
    text: project.name() || String(project.id()),
    value: String(project.id())
  }
}

/** @type {NamedExoticComponent<ProjectsSelectProps>} */
const ProjectsSelectComponent = memo(shapeComponent(ProjectsSelect))

export default ProjectsSelectComponent
